import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, IdCard, Lock } from "lucide-react";
import { usePlatformWhatsApp } from "@/components/PlatformContact";
import { Input, Label } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import type { User } from "@/types";

const card = "rounded-3xl border bg-card p-5 space-y-3";
const pill = "h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold disabled:opacity-50 shrink-0";

export const BookingDetailsForm = ({ onSaved }: { onSaved?: () => void }) => {
  const { t } = usePrefs();
  const { user, setUser } = useAuth();
  const u = user as User;
  const [name, setName] = useState(u.booking_details_set ? u.booking_name || "" : "");
  const [phone, setPhone] = useState(u.phone || "");
  const [busy, setBusy] = useState(false);
  const twoNames = name.trim().split(/\s+/).filter(Boolean).length >= 2;
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.patch("/me/profile", { booking_name: name, phone });
      setUser({ ...u, ...data });
      toast.success(t("bd.saved"));
      onSaved?.();
    } catch (err) {
      toast.error(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-3 text-start" data-testid="booking-details-form">
      <div className="space-y-1.5">
        <Label htmlFor="bd-name">{t("bd.name")}</Label>
        <Input id="bd-name" required value={name} onChange={(e: any) => setName(e.target.value)} placeholder={t("bd.namePh")} data-testid="booking-name-input" className="h-11 rounded-2xl" />
        <p className="text-xs text-muted-foreground">{t("bd.nameHint")}</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="bd-phone">{t("auth.phone")}</Label>
        <Input id="bd-phone" required type="tel" dir="ltr" inputMode="tel" placeholder="07XX XXX XXXX" value={phone} onChange={(e: any) => setPhone(e.target.value)} data-testid="booking-phone-input" className="h-11 rounded-2xl" />
      </div>
      <button data-testid="booking-details-save" disabled={busy || !twoNames || phone.replace(/\D/g, "").length < 10} className="w-full h-11 rounded-full bg-primary text-primary-foreground text-sm font-bold disabled:opacity-50">
        {busy ? t("common.loading") : t("common.save")}
      </button>
    </form>
  );
};

const BookingDetails = () => {
  const { t } = usePrefs();
  const u = useAuth().user as User;
  const [editing, setEditing] = useState(!u.booking_details_set);
  const [wa] = usePlatformWhatsApp();
  const msg = t("bd.changeMsg").replace("{name}", u.booking_name || "").replace("{phone}", u.phone || "").replace("{email}", u.email || "");
  return (
    <div className={card} data-testid="booking-details-card">
      <div className="flex items-center gap-2"><IdCard className="h-5 w-5 text-primary" /><p className="font-bold">{t("bd.title")}</p></div>
      {!editing ? (
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="font-bold" data-testid="booking-details-name">{u.booking_name}</p>
            <p className="text-sm text-muted-foreground" dir="ltr" data-testid="booking-details-phone">{u.phone}</p>
          </div>
          <Lock className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
        </div>
      ) : null}
      {!editing ? (
        <p data-testid="booking-details-locked" className="text-xs text-muted-foreground">
          {t("bd.locked")}{" "}
          {wa && <a href={`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid="booking-details-change-request" className="font-bold text-primary">{t("bd.askChange")}</a>}
        </p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t("bd.sub")}</p>
          <BookingDetailsForm onSaved={() => setEditing(false)} />
        </>
      )}
    </div>
  );
};

const SignInMethods = () => {
  const { t } = usePrefs();
  const u = useAuth().user as User;
  return (
    <div className={card} data-testid="signin-methods-card">
      <p className="font-bold">{t("profile.signin")}</p>
      <div className="flex flex-wrap gap-2 text-xs font-bold">
        {u.has_password && <span className="rounded-full bg-accent px-3 py-1.5 inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" />{t("profile.passwordSet")}</span>}
      </div>
    </div>
  );
};

export const CustomerProfileCards = () => (
  <>
    <BookingDetails />
    <SignInMethods />
  </>
);
